import {
  Building2,
  User,
  Users,
  Landmark,
  GraduationCap,
  Briefcase,
  Shield,
  FileText,
  Check,
} from "lucide-react";

const SECTION_META = [
  { id: "org", label: "Org details", Icon: Building2 },
  { id: "personal", label: "Personal", Icon: User },
  { id: "family", label: "Family", Icon: Users },
  { id: "bank", label: "Bank", Icon: Landmark },
  { id: "educational", label: "Education", Icon: GraduationCap },
  { id: "professional", label: "Profession", Icon: Briefcase },
  { id: "statutory", label: "Statutory", Icon: Shield },
  { id: "documents", label: "Documents", Icon: FileText },
];

/**
 * Vertical (desktop) / horizontal-scroll (mobile) profile section nav.
 * Lock and permission rules mirror the previous Tabs component.
 */
export function ProfileSectionNav({
  activeTab,
  setActiveTab,
  isNewEmployee = false,
  isRejoinPrefill = false,
  isOnboardingMode = false,
  onboardingOrgSubmitted = false,
  isEmployeeView = false,
  employee = {},
  availableTabs = null,
  allowedByPermissionTabs = null,
  sectionComplete = {},
}) {
  const isPfApplicable =
    employee?.pfApplicable === true ||
    employee?.orgDetails?.pfApplicable === true ||
    employee?.statutoryApplicability?.pfApplicable === true;

  const isEsiApplicable =
    employee?.esiApplicable === true ||
    employee?.orgDetails?.esiApplicable === true ||
    employee?.statutoryApplicability?.esiApplicable === true;

  const sections = SECTION_META.filter((s) => {
    if (s.id === "statutory") return isPfApplicable || isEsiApplicable;
    return true;
  });

  const hideWithoutViewAccess =
    isEmployeeView && Array.isArray(allowedByPermissionTabs);

  const toRender = hideWithoutViewAccess
    ? sections.filter((s) => allowedByPermissionTabs.includes(s.id))
    : sections;

  return (
    <nav
      className="profile-section-nav flex shrink-0 flex-row gap-1 overflow-x-auto scroll-area border border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-surface))] p-1.5 max-md:rounded-[11px] md:w-[220px] md:flex-col md:overflow-y-auto md:rounded-[18px] md:p-2.5"
      aria-label="Profile sections"
    >
      {toRender.map(({ id, label, Icon }) => {
        const isOrgFirstLock =
          (isNewEmployee && !isRejoinPrefill && !isOnboardingMode && id !== "org") ||
          (isOnboardingMode && !onboardingOrgSubmitted && id !== "org");

        const isProgressionLocked =
          isEmployeeView &&
          isNewEmployee &&
          !isRejoinPrefill &&
          Array.isArray(availableTabs) &&
          !availableTabs.includes(id);

        const isDisabled = isOrgFirstLock || isProgressionLocked;
        const isActive = activeTab === id;
        const complete = !!sectionComplete[id];

        return (
          <button
            key={id}
            type="button"
            disabled={isDisabled}
            aria-current={isActive ? "true" : undefined}
            title={
              isOrgFirstLock
                ? "Complete Organization Details first"
                : isProgressionLocked
                ? "Complete previous sections to unlock this tab"
                : label
            }
            onClick={() => !isDisabled && setActiveTab(id)}
            className={`flex h-11 shrink-0 items-center gap-2.5 rounded-[11px] px-3 text-left text-sm font-medium transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-[hsl(var(--ds-primary))]/40 md:w-full ${
              isActive
                ? "bg-[hsl(var(--ds-primary)/0.12)] font-semibold text-[hsl(var(--ds-primary))]"
                : isDisabled
                ? "cursor-not-allowed text-[hsl(var(--ds-muted-fg))] opacity-60"
                : "text-[hsl(var(--ds-muted-fg))] hover:bg-[hsl(var(--ds-bg))] hover:text-[hsl(var(--ds-fg))]"
            }`}
          >
            <Icon className="h-5 w-5 shrink-0" strokeWidth={1.7} aria-hidden />
            <span className="truncate whitespace-nowrap">{label}</span>
            <span
              className={`ml-auto grid h-[22px] min-w-[22px] place-items-center rounded-full text-xs ${
                complete
                  ? "text-[hsl(var(--ds-success))]"
                  : "bg-[hsl(var(--ds-warning)/0.2)] text-[hsl(var(--ds-fg))]"
              }`}
              aria-hidden
            >
              {complete ? <Check className="h-3.5 w-3.5" strokeWidth={2.2} /> : "!"}
            </span>
          </button>
        );
      })}
    </nav>
  );
}

export const PROFILE_SECTION_LABELS = Object.fromEntries(
  SECTION_META.map((s) => [s.id, s.label])
);
